import { VossTimeLengthUnits } from '../../../Constants/VOSS/vossTimeLengthUnits';
import { VossBindingPeriodEndAction, VossPeriodEndAnchor, VossPeriodOffsetAnchor } from '../../../Constants/VOSS/vossPlanEnums';
import type {
    BillingPeriodCycle,
    CycleLength,
    PackagePlan,
    PackagePlanRequest,
    PeriodDateOffset,
    PlanBillingCycleDraft,
    PlanDraft,
    PlanOffsetDraft,
    TimeLength,
} from './Packages.types';

// the VOSS contract is a 32-bit int, and Save is a plain button so the browser never validates the number inputs
const MAX_INT32 = 2147483647;

const timeLength = (value: number, unit: VossTimeLengthUnits): Required<TimeLength> => ({ value, unit });

const offset = (value: number, periodAnchor: VossPeriodOffsetAnchor): PlanOffsetDraft => ({ length: timeLength(value, VossTimeLengthUnits.Day), periodAnchor, valueAnchor: null });

const toCycleLength = (length: TimeLength | null | undefined): CycleLength =>
    length?.value === undefined || length.unit === undefined ? null : timeLength(length.value, length.unit);

const toOffset = (source: PeriodDateOffset | null | undefined, fallback: PlanOffsetDraft): PlanOffsetDraft =>
    source === null || source === undefined
        ? fallback
        : {
              length: toCycleLength(source.length) ?? fallback.length,
              periodAnchor: source.periodAnchor ?? fallback.periodAnchor,
              valueAnchor: source.valueAnchor ?? null,
          };

export const emptyBillingCycle = (): PlanBillingCycleDraft => ({
    length: timeLength(1, VossTimeLengthUnits.Month),
    invoiceDateOffset: offset(0, VossPeriodOffsetAnchor.PeriodStart),
    renewalDateOffset: offset(0, VossPeriodOffsetAnchor.PeriodEnd),
    notBillablePeriodRenewalDateOffset: null,
});

export const emptyNotBillableOffset = (): PlanOffsetDraft => offset(0, VossPeriodOffsetAnchor.PeriodEnd);

export const emptyPlanDraft = (): PlanDraft => ({
    name: '',
    externalId: '',
    periodEndAnchor: VossPeriodEndAnchor.RoundedDownToNearestUnit,
    changeBehaviour: null,
    bindingPeriodCycle: { length: timeLength(12, VossTimeLengthUnits.Month), periodEndAction: VossBindingPeriodEndAction.Renew },
    subscriptionBillingPeriodCycle: emptyBillingCycle(),
    usageBillingPeriodCycle: emptyBillingCycle(),
});

const toBillingCycleDraft = (cycle: BillingPeriodCycle | null | undefined): PlanBillingCycleDraft => {
    const fallback = emptyBillingCycle();

    return {
        length: toCycleLength(cycle?.length),
        invoiceDateOffset: toOffset(cycle?.invoiceDateOffset, fallback.invoiceDateOffset),
        renewalDateOffset: toOffset(cycle?.renewalDateOffset, fallback.renewalDateOffset),
        notBillablePeriodRenewalDateOffset:
            cycle?.notBillablePeriodRenewalDateOffset === null || cycle?.notBillablePeriodRenewalDateOffset === undefined
                ? null
                : toOffset(cycle.notBillablePeriodRenewalDateOffset, emptyNotBillableOffset()),
    };
};

export const toPlanDraft = (plan: PackagePlan): PlanDraft => ({
    name: plan.name ?? '',
    externalId: plan.externalId ?? '',
    periodEndAnchor: plan.periodEndAnchor ?? VossPeriodEndAnchor.RoundedDownToNearestUnit,
    changeBehaviour: plan.changeBehaviour ?? null,
    bindingPeriodCycle: {
        length: toCycleLength(plan.bindingPeriodCycle?.length),
        periodEndAction: plan.bindingPeriodCycle?.periodEndAction ?? VossBindingPeriodEndAction.Renew,
    },
    subscriptionBillingPeriodCycle: toBillingCycleDraft(plan.subscriptionBillingPeriodCycle),
    usageBillingPeriodCycle: toBillingCycleDraft(plan.usageBillingPeriodCycle),
});

// a copied plan is a new plan in another package, so the external id must not be carried over
export const toCopiedPlanDraft = (plan: PackagePlan, copySuffix: string): PlanDraft => ({
    ...toPlanDraft(plan),
    name: `${plan.name ?? ''} ${copySuffix}`.trim(),
    externalId: '',
});

const toOffsetRequest = (source: PlanOffsetDraft): PeriodDateOffset => ({
    length: source.length,
    periodAnchor: source.periodAnchor,
    valueAnchor: source.valueAnchor,
});

const toBillingCycleRequest = (cycle: PlanBillingCycleDraft): BillingPeriodCycle => ({
    length: cycle.length ?? undefined,
    invoiceDateOffset: toOffsetRequest(cycle.invoiceDateOffset),
    renewalDateOffset: toOffsetRequest(cycle.renewalDateOffset),
    notBillablePeriodRenewalDateOffset: cycle.notBillablePeriodRenewalDateOffset === null ? undefined : toOffsetRequest(cycle.notBillablePeriodRenewalDateOffset),
});

export const toPlanRequest = (draft: PlanDraft): PackagePlanRequest => ({
    name: draft.name.trim(),
    externalId: draft.externalId.trim().length === 0 ? undefined : draft.externalId.trim(),
    periodEndAnchor: draft.periodEndAnchor,
    changeBehaviour: draft.changeBehaviour ?? undefined,
    bindingPeriodCycle: { length: draft.bindingPeriodCycle.length ?? undefined, periodEndAction: draft.bindingPeriodCycle.periodEndAction },
    subscriptionBillingPeriodCycle: toBillingCycleRequest(draft.subscriptionBillingPeriodCycle),
    usageBillingPeriodCycle: toBillingCycleRequest(draft.usageBillingPeriodCycle),
});

export const isValidLength = (value: number, allowNegative = false): boolean => Number.isInteger(value) && value <= MAX_INT32 && (allowNegative ? value >= -MAX_INT32 : value >= 0);

export const isValidCycleLength = (length: CycleLength): boolean => length === null || (isValidLength(length.value) && length.value > 0);

// VOSS rejects a subscription billing unit that differs from the binding unit; evergreen (null) cycles are exempt
export const hasSubscriptionUnitMismatch = (draft: PlanDraft): boolean => {
    const binding = draft.bindingPeriodCycle.length;
    const subscription = draft.subscriptionBillingPeriodCycle.length;

    return binding !== null && subscription !== null && binding.unit !== subscription.unit;
};

// months are the common unit for Month and Year; a Day length cannot be compared with either, so that pairing is left for VOSS to judge
const MONTHS_PER_UNIT: Partial<Record<VossTimeLengthUnits, number>> = { [VossTimeLengthUnits.Month]: 1, [VossTimeLengthUnits.Year]: 12 };

const comparableLength = (length: Required<TimeLength>): { base: 'days' | 'months'; value: number } => {
    const months = MONTHS_PER_UNIT[length.unit as VossTimeLengthUnits];

    return months === undefined ? { base: 'days', value: length.value } : { base: 'months', value: length.value * months };
};

// VOSS also requires the subscription billing length to be a whole multiple of the usage billing length
export const hasUsageLengthMismatch = (draft: PlanDraft): boolean => {
    const subscription = draft.subscriptionBillingPeriodCycle.length;
    const usage = draft.usageBillingPeriodCycle.length;
    if (subscription === null || usage === null || usage.value <= 0) return false;

    const subscriptionLength = comparableLength(subscription);
    const usageLength = comparableLength(usage);

    return subscriptionLength.base === usageLength.base && subscriptionLength.value % usageLength.value !== 0;
};

// offsets may run backwards (invoice 14 days before period start), cycle lengths may not
const isValidOffset = (source: PlanOffsetDraft): boolean => isValidLength(source.length.value, true) && (source.valueAnchor === null || isValidLength(source.valueAnchor));

const isValidBillingCycle = (cycle: PlanBillingCycleDraft): boolean =>
    isValidCycleLength(cycle.length) &&
    isValidOffset(cycle.invoiceDateOffset) &&
    isValidOffset(cycle.renewalDateOffset) &&
    (cycle.notBillablePeriodRenewalDateOffset === null || isValidOffset(cycle.notBillablePeriodRenewalDateOffset));

export const isValidPlanDraft = (draft: PlanDraft): boolean =>
    draft.name.trim().length > 0 &&
    isValidCycleLength(draft.bindingPeriodCycle.length) &&
    isValidBillingCycle(draft.subscriptionBillingPeriodCycle) &&
    isValidBillingCycle(draft.usageBillingPeriodCycle) &&
    !hasSubscriptionUnitMismatch(draft) &&
    !hasUsageLengthMismatch(draft);
